import React from 'react';

import { ICONS } from '@/constants/icons';

import styles from './ExceptionBadge.module.scss';

/**
 * ExceptionBadge — marks a daily trip date that differs from its series
 * ("eccezione"), with the same glyph and color on every surface: list,
 * timeline, board, user week, generated orders.
 *
 * Domain-agnostic: labels come in already localized.
 *
 * Props:
 * - label:    visible text (e.g. "Eccezione", "16/09"); omitted in `dot` variant
 * - count:    number shown instead of the label (`count` variant: "2 eccezioni")
 * - detail:   full sentence for the tooltip and screen readers
 *             (e.g. "Eccezione del 16/09 — orario, autista")
 * - variant:  inline (default: icon + label) | count (icon + number) | dot (icon only)
 * - size:     xs | sm (default)
 *
 * Color is never the only signal: the glyph and the text/aria-label say it too.
 * Uses `title`, not a mounted tooltip: it renders once per timeline item.
 */
export default function ExceptionBadge({
  label,
  count,
  detail,
  variant = 'inline',
  size = 'sm',
  className = '',
}) {
  const text = variant === 'count' ? String(count ?? '') : variant === 'dot' ? '' : label;
  const a11y = detail || label || (count != null ? String(count) : '');

  return (
    <span
      className={`${styles.badge} ${styles[variant] || ''} ${styles[size] || styles.sm} ${className}`.trim()}
      title={detail || undefined}
      aria-label={a11y || undefined}
      role={a11y ? 'img' : undefined}
    >
      <i className={ICONS.exception} aria-hidden="true" />
      {text ? <span aria-hidden="true">{text}</span> : null}
    </span>
  );
}
